import { editorToolbar, editorPages, editorImages } from "./editorOptions";
import { useEffect, useRef, useState } from "react";
import type { Editor } from "hugerte";
import { v4 as uuidv4 } from "uuid";

import bootstrapContentCss from "bootstrap/dist/css/bootstrap.min.css?raw";
import hugerteContentCss from "hugerte/skins/content/default/content.min.css?raw";
import hugerteSkinCss from "hugerte/skins/ui/oxide/skin.min.css?raw";
import hugerteDarkSkinCss from "hugerte/skins/ui/oxide-dark/skin.min.css?raw";
import { useTheme } from "../../../context/ThemeProvider";
import editorSandboxShell from "../../../../public/editor-sandbox.html?raw";

import { ContentService } from "../../../services/ContentService";
import { MediaService } from "../../../services/MediaService";
import { ScriptAndStyleService } from "../../../services/ScriptAndStyleService";
import { IEditorSiteFrame } from "../../../models/Interfaces";
import {
  buildEditorContentStyle,
  escapeStyleClose,
  frameHasLayers,
  loadSiteEditorStyles
} from "./editorSiteFrame";
import {
  EDITOR_CHILD_CHANGE,
  EDITOR_CHILD_LOADED,
  EDITOR_PARENT_STYLES,
  EDITOR_CHILD_HEIGHT,
  EDITOR_CHILD_FULLSCREEN,
  EDITOR_CHILD_OVERLAY,
  EDITOR_CHILD_READY,
  EDITOR_PARENT_INIT,
  EDITOR_PARENT_LISTS,
  EDITOR_PARENT_SET,
  enabledClientScriptCode,
  type IEditorMenuMessage
} from "./editorSandboxProtocol";
import { buildEditorSandboxDocument } from "./editorSandboxDocument";

interface IHTMLEditor {
  preview?: boolean;
  documentKey?: string | number;
  id?: string;
  name?: string;
  value: string;
  height?: number;
  onChange?: (content: string) => void;
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  menuItems?: Array<IEditorMenu>;
  siteFrame?: IEditorSiteFrame;
}

export interface IEditorMenu {
  Name: string;
  ButtonText: string;
  Items: Array<IEditorMenuItem>
}

export interface IEditorMenuItem {
  text: string;
  insertText?: string;
  onAction: (editorInstance: Editor) => void;
}

const CHROME_VARS = [
  "--font-family-base",
  "--color-white",
  "--color-brand-700",
  "--color-brand-500",
  "--color-brand-100",
  "--color-neutral-900",
  "--color-neutral-700",
  "--color-neutral-500",
  "--color-neutral-200",
  "--color-neutral-100",
  "--color-neutral-50",
  "--app-surface",
  "--app-surface-muted",
  "--app-border",
  "--text-primary",
  "--text-secondary",
  "--text-muted",
  "--brand-primary",
  "--brand-primary-strong",
  "--brand-primary-soft"
];

const chromeVariables = (): string => {
  const styles = getComputedStyle(document.documentElement);
  return CHROME_VARS
    .map((name) => `${name}: ${styles.getPropertyValue(name).trim()};`)
    .filter((line) => !line.endsWith(": ;"))
    .join("");
};

const absoluteUrl = (url: string): string => {
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
};

/** Read a stylesheet from the admin page. The editor frame cannot load it itself. */
const inlineRemoteCss = async (urls: string[]): Promise<string> => {
  const parts = await Promise.all(urls.map(async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        return "";
      }

      const type = response.headers.get("content-type") ?? "";
      if (type.includes("text/html")) {
        return "";
      }

      return await response.text();
    } catch {
      return "";
    }
  }));

  return parts.filter((part) => part.trim().length > 0).join("\n");
};

const HTMLEditor = (props: IHTMLEditor) => {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(props.preview === false);
  const [hasOpened, setHasOpened] = useState(props.preview === false);
  const [previewHeight, setPreviewHeight] = useState(100);
  const [previewStyle, setPreviewStyle] = useState(hugerteContentCss);
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const sendTheme = () => iframeRef.current?.contentWindow?.postMessage({
    type: "sbcms-editor-theme", editorId,
    skinCss: themeRef.current === "midnight" ? hugerteDarkSkinCss : hugerteSkinCss,
    chromeVars: chromeVariables()
  }, "*");
  const contentSvcRef = useRef<ContentService>();
  const mediaSvcRef = useRef<MediaService>();
  
  const [editorId] = useState<string>(props.id ?? uuidv4());
  const customToolbarButtons = props.menuItems?.map((item) => item.Name).join(" | ") ?? "";
  const toolbarConfig = editorToolbar(customToolbarButtons);
  const getPages = async () => contentSvcRef.current ? editorPages(contentSvcRef.current) : null;
  const getImages = async () => mediaSvcRef.current ? editorImages(mediaSvcRef.current) : null;

  const frameRef = useRef(props.siteFrame);
  const valueRef = useRef(props.value);
  const menuRef = useRef(props.menuItems);
  const onChangeRef = useRef(props.onChange);
  const onInputRef = useRef(props.onInputChange);
  const nameRef = useRef(props.name);
  frameRef.current = props.siteFrame;
  menuRef.current = props.menuItems;
  onChangeRef.current = props.onChange;
  onInputRef.current = props.onInputChange;
  nameRef.current = props.name;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastHtmlRef = useRef(props.value);
  const lastDocumentKeyRef = useRef(props.documentKey);
  const [frameHeight, setFrameHeight] = useState(props.height && props.height > 0 ? props.height : 220);
  const [editorReady, setEditorReady] = useState(false);
  const [overlay, setOverlay] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const fullscreenRef = useRef(false);
  // Data URL keeps an opaque origin. allow-same-origin lets HugeRTE edit its
  // content frame without giving that frame the admin page.
  const [sandboxSrc, setSandboxSrc] = useState("");

  useEffect(() => {
    // Bundled shell. Fetching editor-sandbox.html follows the host's redirect
    // onto the admin app, and that document cannot load its scripts from here.
    setSandboxSrc(buildEditorSandboxDocument(
      editorSandboxShell,
      themeRef.current === "midnight" ? hugerteDarkSkinCss : hugerteSkinCss,
      window.location.origin,
      editorId
    ));
  }, [editorId]);

  useEffect(() => {
    const frame = requestAnimationFrame(sendTheme);
    return () => cancelAnimationFrame(frame);
  }, [theme]);

  useEffect(() => {
    valueRef.current = props.value;
  }, [props.value]);

  useEffect(() => {
    let cancelled = false;
    const iframeWindow = () => iframeRef.current?.contentWindow ?? null;
    const post = (message: Record<string, unknown>) => {
      iframeWindow()?.postMessage(message, "*");
    };

    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }

    if (!mediaSvcRef.current) {
      mediaSvcRef.current = new MediaService();
    }

    const lists: { pageList: unknown[] | null; imageList: unknown[] | null } = {
      pageList: null,
      imageList: null
    };
    let initSent = false;
    let iframeReady = false;
    let stylesReady = false;
    const localFrame = frameRef.current;
    let contentStyle = [
      escapeStyleClose(hugerteContentCss),
      frameHasLayers(localFrame) ? escapeStyleClose(bootstrapContentCss) : "",
      buildEditorContentStyle({ pageCss: localFrame?.pageCss, themed: frameHasLayers(localFrame) || Boolean(localFrame?.pageCss) })
    ].filter(Boolean).join("\n");
    let scripts: string[] = [];

    const sendLists = () => {
      if (!initSent) {
        return;
      }

      post({
        type: EDITOR_PARENT_LISTS,
        editorId,
        pageList: lists.pageList,
        imageList: lists.imageList
      });
    };

    const sendInit = () => {
      if (cancelled || !iframeReady || initSent) {
        return;
      }

      initSent = true;
      const menus: IEditorMenuMessage[] = (menuRef.current ?? []).map((menu) => ({
        name: menu.Name,
        text: menu.ButtonText,
        items: menu.Items
          .filter((item) => item.insertText)
          .map((item) => ({ text: item.text, insertText: item.insertText ?? "" }))
      }));

      post({
        type: EDITOR_PARENT_INIT,
        editorId,
        html: valueRef.current ?? "",
        height: props.height && props.height > 0 ? props.height : 200,
        toolbar: toolbarConfig,
        contentStyle,
        contentCss: false,
        frame: frameRef.current ?? null,
        scripts,
        assetsReady: stylesReady,
        pageList: lists.pageList,
        imageList: lists.imageList,
        menus,
        chromeVars: chromeVariables()
      });
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeWindow() || event.origin !== "null") {
        return;
      }

      const data = event.data as { type?: string; editorId?: string; html?: string; open?: boolean; height?: number; state?: boolean } | null;
      if (!data || data.editorId !== editorId) {
        return;
      }

      if (data.type === EDITOR_CHILD_READY) {
        iframeReady = true;
        sendTheme();
        sendInit();
        return;
      }

      if (data.type === EDITOR_CHILD_LOADED) {
        setEditorReady(true);
        return;
      }

      if (data.type === EDITOR_CHILD_CHANGE && typeof data.html === "string") {
        lastHtmlRef.current = data.html;
        if (onChangeRef.current) {
          onChangeRef.current(data.html);
        }

        if (onInputRef.current) {
          const change = { target: { value: data.html, name: nameRef.current || "HTMLEditor" } };
          onInputRef.current(change as unknown as React.ChangeEvent<HTMLInputElement>);
        }
        return;
      }

      if (data.type === EDITOR_CHILD_OVERLAY) {
        setOverlay(Boolean(data.open));
        return;
      }

      if (data.type === EDITOR_CHILD_FULLSCREEN) {
        fullscreenRef.current = Boolean(data.open);
        setFullscreen(Boolean(data.open));
        return;
      }

      if (data.type === EDITOR_CHILD_HEIGHT && !fullscreenRef.current && typeof data.height === "number" && data.height > 0) {
        setFrameHeight(data.height);
      }
    };

    window.addEventListener("message", onMessage);

    const styleService = new ScriptAndStyleService();
    const siteKey = styleService.siteKey ?? "";
    const stylesPromise = siteKey
      ? loadSiteEditorStyles(siteKey, () => styleService.getCss())
      : Promise.resolve({ css: "", urls: [] as string[] });
    const scriptsPromise = siteKey ? styleService.getScripts() : Promise.resolve([]);

    void Promise.all([stylesPromise, scriptsPromise]).then(async ([siteStyles, clientScripts]) => {
      if (cancelled) {
        return;
      }

      const frame = frameRef.current;
      const themed = Boolean(
        siteStyles.css.trim()
        || siteStyles.urls.length
        || frame?.pageCss?.trim()
        || frameHasLayers(frame)
      );
      const remoteCss = themed
        ? await inlineRemoteCss(siteStyles.urls.map(absoluteUrl))
        : "";
      if (cancelled) {
        return;
      }

      // Styles are text inside the frame. A stylesheet address would be loaded
      // from the data document and blocked without a CORS header.
      contentStyle = [
        escapeStyleClose(hugerteContentCss),
        themed ? escapeStyleClose(bootstrapContentCss) : "",
        escapeStyleClose(remoteCss),
        buildEditorContentStyle({
          siteCss: siteStyles.css,
          pageCss: frame?.pageCss,
          themed
        })
      ].filter((part) => part.trim().length > 0).join("\n");
      setPreviewStyle(contentStyle);
      scripts = enabledClientScriptCode(clientScripts);
      stylesReady = true;
      if (initSent) post({ type: EDITOR_PARENT_STYLES, editorId, contentStyle, scripts });
      else sendInit();
    }).catch(() => {
      if (!cancelled) {
        stylesReady = true;
        if (initSent) post({ type: EDITOR_PARENT_STYLES, editorId, contentStyle, scripts });
        else sendInit();
      }
    });

    void getPages().then((pages) => {
      if (cancelled) {
        return;
      }

      lists.pageList = pages;
      sendLists();
    });

    void getImages().then((images) => {
      if (cancelled) {
        return;
      }

      lists.imageList = images;
      sendLists();
    });

    return () => {
      cancelled = true;
      window.removeEventListener("message", onMessage);
    };
  }, [editorId, toolbarConfig]);

  useEffect(() => {
    const documentChanged = props.documentKey !== lastDocumentKeyRef.current;
    if (!documentChanged && props.value === lastHtmlRef.current) return;
    lastDocumentKeyRef.current = props.documentKey;
    if (documentChanged && props.preview !== false) setEditing(false);
    lastHtmlRef.current = props.value;
    iframeRef.current?.contentWindow?.postMessage({
      type: EDITOR_PARENT_SET,
      editorId,
      html: props.value ?? "",
      resetHistory: documentChanged,
      ...(documentChanged ? { frame: frameRef.current ?? null } : {})
    }, "*");
  }, [props.value, props.documentKey, editorId]);

  useEffect(() => {
    if (!editing || props.preview === false || overlay || fullscreen) return;
    const returnToPreview = (event: Event) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) setEditing(false);
    };
    document.addEventListener("pointerdown", returnToPreview, true);
    document.addEventListener("focusin", returnToPreview, true);
    return () => {
      document.removeEventListener("pointerdown", returnToPreview, true);
      document.removeEventListener("focusin", returnToPreview, true);
    };
  }, [editing, props.preview, overlay, fullscreen]);

  const openEditor = () => { setHasOpened(true); setEditing(true); };
  const previewDocument = `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="script-src 'none'; object-src 'none'; frame-src 'none'; form-action 'none'"><style>${escapeStyleClose(previewStyle)}\nhtml,body{margin:0!important;padding:8px!important;box-sizing:border-box;min-height:0!important;height:auto!important}</style></head><body>${props.value ?? ""}</body></html>`;
  return (
    <div ref={containerRef} className="sbcms-rich-text">
      {props.preview !== false && !editing && <div className="sbcms-rich-text-preview">
        {props.value?.trim() ? <iframe title="Content preview" sandbox="allow-same-origin" tabIndex={-1} srcDoc={previewDocument} style={{ height: previewHeight }}
          onLoad={event => {
            const frame = event.currentTarget;
            const measure = () => {
              try { setPreviewHeight(Math.min(360, Math.max(80, (frame.contentDocument?.body.scrollHeight ?? 84) + 16))); } catch { /* A stored redirect cannot expose its document. */ }
            };
            measure();
            void frame.contentDocument?.fonts?.ready.then(measure);
          }} /> : <div className="sbcms-rich-text-preview__empty">No content yet</div>}
        <button type="button" className="sbcms-rich-text-preview__edit" onClick={openEditor}>
          <span>Edit content</span>
        </button>
      </div>}
    <div hidden={!editing}
      className={fullscreen ? "sbcms-editor-frame is-fullscreen" : overlay ? "sbcms-editor-frame is-overlay" : "sbcms-editor-frame"}
      style={fullscreen || overlay ? undefined : { height: frameHeight }}
    >
      {hasOpened && !editorReady && <div className="sbcms-editor-loading" role="status" aria-live="polite">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        <span>Loading editor…</span>
      </div>}
      {hasOpened && sandboxSrc ? (
        <iframe
          ref={iframeRef}
          title="HTML editor"
          sandbox="allow-scripts allow-same-origin"
          src={sandboxSrc}
        />
      ) : null}
    </div>
      {props.preview !== false && editing && <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setEditing(false)}>Done editing</button>}
    </div>
  );
};

export default HTMLEditor;
